import { StoryblokComponent } from '@storyblok/react';
import React from 'react';

import { usePreviewMode, useStoryblok } from '@b2c-web-marketing/shared/storyblok-sdk';
import { dehydrate } from '@tanstack/react-query';
import {
  PageItem,
  getBlogCategory,
  getCookieBanner,
  getFooter,
  getHeader,
  getLocationModal,
  getSharedSeoSchemaItems,
} from 'apps/urban/api';
import { componentRelations } from 'apps/urban/api/relations/relations';
import { Layout } from 'apps/urban/components/Layout/Layout';
import { queryClient } from 'apps/urban/context/ReactQueryProvider';
import { getDynamicItemsData } from 'apps/urban/utils/getDynamicComponent/getDynamicComponent';
import { getTemplateType } from 'apps/urban/utils/getTemplateType';
import { handlePageItemLoad } from 'apps/urban/utils/handlePageItemLoad/handlePageItemLoad';
import { NEXT_ISR_REVALIDATE_SECONDS } from 'apps/urban/utils/isr';
import { Router } from 'next/router';

import { getFooterV2 } from '../../api/utils/getFooterV2';
import { getHeaderV2 } from '../../api/utils/getHeaderV2';
import { getSettings } from '../../api/utils/getSettings';
import { getServiceOSGlobalSettings } from '../../utils/getServiceOSGlobalSettings';
import { getURLFromStoryblokLink } from '../../utils/getURLFromStoryblokLink/getURLFromStoryblokLink';

// Track client-side page views with Segment
Router.events.on('routeChangeComplete', (url) => {
  window?.analytics?.page(url);
});

const BlogPage = ({
  data,
  preview,
  header,
  footer,
  locationModal,
  cookieBanner,
  useV2Header,
  useV2Footer,
  keepTopMargin,
}) => {
  usePreviewMode(preview);
  const story = useStoryblok<PageItem>(data, preview, handlePageItemLoad, componentRelations);

  if (!data || !story?.content) {
    return null;
  }

  return (
    <Layout
      header={header}
      footer={footer}
      metatags={data?.content?.metatags}
      locationModal={locationModal}
      cookieBanner={cookieBanner}
      noIndex={data?.content?.noIndex}
      noFollow={data?.content?.noFollow}
      useV2Header={useV2Header}
      useV2Footer={useV2Footer}
      keepTopMargin={keepTopMargin}
    >
      <StoryblokComponent blok={story.content} />
    </Layout>
  );
};

export default BlogPage;

export const getStaticProps = async ({ locale, preview = false }) => {
  locale = locale === 'default' ? 'en-gb' : locale;

  const templateType = await getTemplateType({ params: { slug: ['blog'] }, locale: locale });

  let data = null;

  if (templateType === 'blogCategory') {
    data = await getBlogCategory({
      slug: `${locale}/blog/`,
      relations: componentRelations,
    });
  }
  const client = await getDynamicItemsData({ story: data, queryClient, locale });

  const dehydratedState = client ? dehydrate(client) : null;

  const settings = await getSettings({ slug: `${locale}/settings` });
  const headerV2Settings = settings?.content?.headerV2SettingsPerPage?.find(
    (setting) => getURLFromStoryblokLink(setting.link) === `${locale}/blog/`
  );

  const useV2Header = (settings?.content?.useV2Header && headerV2Settings?.useV2Header) || false;
  const useV2Footer = settings?.content?.useV2Footer || false;
  const keepTopMargin = useV2Header && headerV2Settings?.keepTopMargin;
  const keepSolidBackground = useV2Header && headerV2Settings?.keepSolidBackground;

  const header = useV2Header
    ? await getHeaderV2({ slug: `${locale}/header-v2` })
    : await getHeader({ slug: `${locale}/header` });
  const footer = useV2Footer
    ? await getFooterV2({ slug: `${locale}/footer-v2`, relations: componentRelations })
    : await getFooter({ slug: `${locale}/footer` });
  const locationModal = await getLocationModal({ slug: `${locale}/location-modal` });
  const cookieBanner = await getCookieBanner({ slug: `${locale}/cookie-consent` });
  const sharedseoschemaItems = await getSharedSeoSchemaItems({
    startsWith: `${locale}/shared-schemas`,
  });

  console.log(`getStaticProps Blog ${locale} revalidate: ${NEXT_ISR_REVALIDATE_SECONDS}`);

  return {
    props: {
      sharedseoschemaItems: sharedseoschemaItems || null,
      cookieBanner: cookieBanner ? cookieBanner.content : null,
      footer: footer.content,
      header: { ...header.content, keepSolidBackground },
      serviceOS: getServiceOSGlobalSettings(settings),
      locationModal: locationModal.content,
      preview,
      data,
      dehydratedState,
      useV2Header,
      useV2Footer,
      keepTopMargin,
    },
    revalidate: NEXT_ISR_REVALIDATE_SECONDS,
  };
};
